import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiSDK } from '../../contexts/apiSdkContext';
import { useAreaId } from '../../contexts/CompaniesAreasContext';
import { AppContext } from '../../pages/layout';

type TAreaDeleteHook = {
  isError: boolean;
  isLoading: boolean;
  mutate: UseMutationResult<void, unknown, string, unknown>['mutate'];
};

function useDeleteArea(): TAreaDeleteHook {
  const sdk = useApiSDK();
  const appContext = useContext(AppContext);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { areaId, resetAreaId } = useAreaId();

  const { isError, isLoading, mutate } = useMutation({
    mutationFn: async (id: string) => {
      await sdk.areas.delete(id);
    },
    onSuccess: async (_contexts, variables) => {
      await queryClient.invalidateQueries(['areas']);
      await queryClient.invalidateQueries(['company-areas']);
      appContext?.crudAlert('delete', 'area', 'f');

      if (variables === areaId?.id || areaId?.id === 'ALL') {
        resetAreaId();
      }

      navigate('/areas');
    },
    onError: async (error: any) => {
      if (error) {
        const responseErrors = error.response.data.errors;
        responseErrors.forEach((err: { message: string; description: string }) => {
          appContext?.systemAlert('error', err.description);
        });
      }
    },
  });

  return { isError, isLoading, mutate };
}

export { useDeleteArea };
